/**
 * Shared German labels for audit-history field names (admin entity detail pages).
 * Entity pages spread this and add overrides for entity-specific fields.
 */
export const COMMON_AUDIT_FIELD_LABELS = {
  name: "Name",
  email: "E-Mail",
  phone: "Telefonnummer",
  status: "Status",
  address_line1: "Adresse",
  postal_code: "PLZ",
  city: "Ort",
  canton: "Kanton",
  notes: "Notizen",
};

/** Per-entity field labels for audit diff display (falls back to COMMON_AUDIT_FIELD_LABELS via getAuditFieldLabel). */
export const ENTITY_AUDIT_LABELS = {
  unit: {
    landlord_id: "Verwaltung",
    property_manager_id: "Bewirtschafter",
    owner_id: "Eigentümer",
    property_id: "Liegenschaft",
    title: "Titel",
    address: "Adresse",
    city: "Ort",
    postal_code: "PLZ",
    rooms: "Zimmeranzahl",
    type: "Typ",
    tenant_price_monthly_chf: "Geplante Miete",
    landlord_rent_monthly_chf: "Mietkosten Vermieter",
    utilities_monthly_chf: "Nebenkosten",
    cleaning_cost_monthly_chf: "Reinigung",
    occupancy_status: "Belegungsstatus",
    occupied_rooms: "belegte Zimmer",
    landlord_lease_start_date: "Mietstart Vermieter",
    available_from: "Verfügbar ab",
    landlord_deposit_type: "Kautionsart Vermieter",
    landlord_deposit_amount: "Kaution Vermieter",
    landlord_deposit_annual_premium: "Kautionsprämie",
    landlord_deposit_provider: "Kautionsanbieter Vermieter",
  },
  inventory_item: {
    inventory_number: "Inventarnummer",
    name: "Name",
    category: "Kategorie",
    brand: "Marke",
    total_quantity: "Gesamtmenge",
    condition: "Zustand",
    status: "Status",
    purchase_price_chf: "Anschaffung CHF",
    purchase_date: "Kaufdatum",
    purchased_from: "Gekauft bei",
    supplier_article_number: "Lieferanten-Artikelnr.",
    product_url: "Produkt-URL",
    notes: "Notizen",
  },
  landlord: {
    ...COMMON_AUDIT_FIELD_LABELS,
    user_id: "Portal-Benutzer",
    company_name: "Firmenname",
    contact_name: "Kontaktperson",
    website: "Website",
    deleted_at: "Archivierung",
  },
  property_manager: {
    ...COMMON_AUDIT_FIELD_LABELS,
    landlord_id: "Verwaltung",
  },
  owner: {
    ...COMMON_AUDIT_FIELD_LABELS,
  },
  property: {
    ...COMMON_AUDIT_FIELD_LABELS,
    title: "Titel",
    street: "Strasse",
    house_number: "Hausnummer",
    zip_code: "PLZ",
    city: "Ort",
    country: "Land",
    landlord_id: "Vermieter",
    lat: "Breitengrad",
    lng: "Längengrad",
    deleted_at: "Archivierung",
  },
  tenant: {
    ...COMMON_AUDIT_FIELD_LABELS,
    first_name: "Vorname",
    last_name: "Nachname",
    // Same wording as the backend's TenantEvent label, so the two tenant
    // history surfaces read alike. Named after the opt-out rather than the
    // greeting: the audit diff shows Ja/Nein, and "Geburtstagsgrüsse" would
    // leave the reader guessing which way the switch went.
    birthday_email_opt_out: "Geburtstags-E-Mail-Opt-out",
  },
  application: {
    ...COMMON_AUDIT_FIELD_LABELS,
    status: "Status",
    applicant_type: "Bewerbertyp",
    salutation: "Anrede",
    first_name: "Vorname",
    last_name: "Nachname",
    email: "E-Mail",
    phone: "Telefon",
    birth_date: "Geburtsdatum",
    nationality: "Nationalität",
    residence_permit: "Aufenthaltsbewilligung",
    address_street: "Strasse",
    address_zip: "PLZ",
    address_city: "Ort",
    company_name: "Firmenname",
    company_registration_no: "HR-/UID-Nummer",
    company_position: "Position Kontaktperson",
    occupation: "Beschäftigung",
    occupation_other: "Beschäftigung (Sonstiges)",
    employer: "Arbeitgeber",
    gross_income: "Bruttoeinkommen",
    work_style: "Arbeitsweise",
    move_in_date: "Einzugsdatum",
    rent_duration: "Mietdauer",
    debt_collections: "Betreibungen (je gehabt)",
    open_collections: "Offene Betreibungen",
    loss_certificates: "Verlustscheine",
    financial_notes: "Finanz-Anmerkungen",
    wg_experience: "WG-Erfahrung",
    wg_experience_duration: "Erfahrungsdauer",
    day_rhythm: "Tagesrhythmus",
    social_style: "Sozialer Stil",
    smoker: "Raucher",
    cleanliness: "Ordentlichkeit",
    pets: "Haustiere",
    pets_details: "Haustier-Details",
    visitors: "Besuch",
    wg_rules_accepted: "WG-Regeln akzeptiert",
    self_description: "Selbstbeschreibung",
    motivation_room: "Motivation Zimmer",
    motivation_wg: "Motivation WG",
    credit_check_consent: "Bonitätsprüfung erlaubt",
    reference_check_consent: "Referenzauskünfte erlaubt",
    confirm_correct: "Angaben bestätigt",
    privacy_accepted: "Datenschutz akzeptiert",
    submitted_ip: "Submitted IP",
    submitted_user_agent: "User-Agent",
    pdf_object_key: "PDF-Objekt",
    email_sent: "E-Mail versendet",
    updated_at: "Aktualisiert",
    listing_id: "Inserat",
  },
};

/**
 * @param {string} entityType - e.g. unit, landlord, inventory_item
 * @param {string} fieldKey
 * @returns {string}
 */
export function getAuditFieldLabel(entityType, fieldKey) {
  const entityMap = ENTITY_AUDIT_LABELS[entityType];
  if (entityMap && entityMap[fieldKey]) return entityMap[fieldKey];
  if (COMMON_AUDIT_FIELD_LABELS[fieldKey]) return COMMON_AUDIT_FIELD_LABELS[fieldKey];
  return fieldKey;
}
